import { FC, PropsWithChildren, ReactNode, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Paper, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import ArrowRightOutlined from "@mui/icons-material/ArrowRightOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface ILinkBoxProps {
  title: string;
  description: string;
  icon: ReactNode;
  link?: {
    label: string;
    url: string;
  };
  buttonLink?: boolean;
  disabled: boolean;
  list?: string[];
  sx?: SxProps<Theme>;
  dataTestId?: string;
}

const LinkBox: FC<PropsWithChildren<ILinkBoxProps>> = ({
  children,
  title,
  description,
  link,
  buttonLink = false,
  icon,
  disabled,
  list = [],
  sx = {},
  dataTestId,
}) => (
  <Paper variant="LinkBox" sx={sx} data-testid={dataTestId}>
    <Box
      component={Link}
      to={link?.url || window.location.pathname}
      aria-disabled={disabled}
      onClick={disabled ? (e: MouseEvent) => e.preventDefault() : undefined}
      data-testid={dataTestId ? `${dataTestId}-header-link` : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        mb: "1rem",
        color: "text.primary",
        textDecoration: "none",
        ...(disabled && { cursor: "default", pointerEvents: "none" }),
      }}
    >
      <Box
        component="span"
        sx={{
          width: "2rem",
          height: "2rem",
          display: "block",
          flexShrink: "0",
          bgcolor: "grey.200",
          padding: "4px 5px 5px 7px",
          marginRight: "1rem",
          borderRadius: "50%",
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h6"
        component="h2"
        color={disabled ? "textDisabled" : "textPrimary"}
      >
        {title}
      </Typography>
      <ArrowForwardIosIcon fontSize="small" sx={{ ml: "auto" }} />
    </Box>
    <Typography
      variant="body2"
      color={disabled ? "textDisabled" : "textSecondary"}
    >
      {description}
    </Typography>
    {list.length > 0 && (
      <Box component="ul" sx={{ listStyleType: "disc", pl: 3, mt: 1 }}>
        {list.map((item) => (
          <Box component="li" key={item}>
            <Typography
              variant="body2"
              color={disabled ? "textDisabled" : "textSecondary"}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
    {children}
    {buttonLink && (
      <Button
        component={Link}
        variant="contained"
        to={link?.url || window.location.pathname}
        endIcon={
          <ArrowRightOutlined fontSize="large" htmlColor="text.secondary" />
        }
        color="text.primary"
        size="large"
        fullWidth
        disableElevation
        disabled={disabled}
        data-testid={dataTestId ? `${dataTestId}-button-link` : undefined}
        sx={{
          justifyContent: "space-between",
          bgcolor: "grey.100",
          fontWeight: 400,
          letterSpacing: "normal",
          border: "1px solid",
          borderColor: "grey.200",
          padding: "1rem",
          mt: "auto",

          "&:hover": {
            bgcolor: "grey.300",
            color: "text.primary",
          },
        }}
      >
        {link?.label}
      </Button>
    )}
  </Paper>
);

export default LinkBox;
